import { Chevron } from "@components/icons"
import breakpoints from "@constants/breakpoints"
import colors from "@constants/colors"
import {
  body14BoldStyles,
  body14Styles,
  body16BoldStyles,
  body16MainStyles,
  h4Styles,
  smallcapsBold13Styles,
} from "@constants/typography"
import { DesktopOnly, MobileAndTabletOnly } from "@utils/MediaQuery"
import React from "react"
import styled from "styled-components"

import Modal, { ModalBaseProps } from "../index"

const Container = styled.div`
  display: flex;
  flex-direction: column;
`

const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 72px;
  margin-bottom: 40px;
  border-bottom: 1px solid ${colors.dividerLineGray};
  padding: 0 20px;

  @media ${breakpoints.laptop} {
    height: 56px;
    margin-bottom: 85px;
    border-bottom: none;
    padding: 0;
  }
`

const HeaderButton = styled.button`
  cursor: pointer;
  background: none;
  border: none;
  outline: none;
  height: 32px;
  width: 32px;
  transform: rotate(180deg) translateY(1px);
  padding: 0;
  margin-right: 8px;

  @media ${breakpoints.laptop} {
    display: none;
  }
`

const HeaderText = styled.div`
  ${smallcapsBold13Styles}

  @media ${breakpoints.laptop} {
    ${h4Styles}
  }
`

const Content = styled.div`
  display: flex;
  flex-direction: column;

  @media ${breakpoints.laptop} {
    flex-direction: row;
    flex-wrap: wrap;
  }
`

const Col = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  @media (min-width: 1024px) {
    width: calc(4 / 12 * 100vw);
    padding-right: calc(1 / 12 * 100vw);
  }

  @media (min-width: 1768px) {
    width: calc(2.5 / 12 * 100vw);
    padding-right: calc(0.5 / 12 * 100vw);
  }
`

const SpecItem = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1 0 auto;
  align-items: center;
  min-height: 56px;
  border-bottom: 1px solid ${colors.dividerLineGray};
  padding: 0 20px;

  @media ${breakpoints.laptop} {
    padding: 16px 0;
  }
`

const SpecItemKey = styled.div`
  display: flex;
  flex-shrink: 0;
  flex-wrap: wrap;
  ${body14BoldStyles}
  width: 111.62px;
  margin-right: 20px;

  @media ${breakpoints.laptop} {
    ${body16BoldStyles}
    margin-right: 40px;
  }
`

const SpecItemValue = styled.div`
  display: flex;
  flex-wrap: wrap;
  ${body14Styles}
  color: ${colors.mediumGray};

  @media ${breakpoints.laptop} {
    ${body16MainStyles}
    color: ${colors.mediumGray};
  }
`

interface ProductSpecsModalProps extends ModalBaseProps {
  specs: Array<{
    body: string
    title: string
  }>
}

const renderSpecRow = (spec: { body: string; title: string }) => (
  <Col key={spec.title}>
    <SpecItem>
      <SpecItemKey>{spec.title}</SpecItemKey>
      <SpecItemValue>{spec.body}</SpecItemValue>
    </SpecItem>
  </Col>
)

const ProductSpecsModal = ({ specs, ...rest }: ProductSpecsModalProps) => {
  return (
    <Modal {...rest}>
      <Container>
        <MobileAndTabletOnly>
          <Header onClick={rest.onRequestClose}>
            <HeaderButton>
              <Chevron strokeColor={colors.black} />
            </HeaderButton>
            <HeaderText>Specifications</HeaderText>
          </Header>
        </MobileAndTabletOnly>
        <DesktopOnly>
          <Header>
            <HeaderText>Specifications</HeaderText>
          </Header>
        </DesktopOnly>
        <Content>{specs.map(renderSpecRow)}</Content>
      </Container>
    </Modal>
  )
}

export default ProductSpecsModal
